/**
 * @fileoverview QR Code landing page component.
 * @summary Handles QR code token validation and allows joining event (guest or authenticated).
 *
 * @package KaraoNow
 * @module apps/front/src/pages/QRCodeLandingPage
 * @author Pierre-Yves Léglise <contact@axialdata.net>
 * @license EULA
 */
import { useEffect, useState } from 'react'
import { useParams, useNavigate, Link } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faMusic, faUser, faUserPlus, faSpinner } from '@fortawesome/free-solid-svg-icons'
import { useAppSelector } from '../hooks/redux.js'
import { apiRequest } from '../services/api.js'
import { API_ENDPOINTS } from '../config/api.js'
import FloatingLabelInput from '../components/FloatingLabelInput.js'
import Spinner from '../components/Spinner.js'
import { ConfirmModal } from '../components/ConfirmModal.js'
import { ADMIN_DASHBOARD_URL } from '@/config/constants.js'

/**
 * @summary Event data from QR validation.
 */
interface EventData {
  eventId: string
  eventName: string
  eventDate?: string
  eventStatus?: 'draft' | 'active' | 'completed' | 'cancelled'
  venueName?: string
}

/**
 * @summary QR Code landing page component.
 * @returns {React.ReactElement} QR Code landing page.
 * @author Pierre-Yves Léglise <contact@axialdata.net>
 */
export default function QRCodeLandingPage() {
  const { t } = useTranslation()
  const { token } = useParams<{ token: string }>()
  const navigate = useNavigate()
  const { isAuthenticated, user } = useAppSelector((state) => state.auth)

  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [eventData, setEventData] = useState<EventData | null>(null)

  // Guest mode state
  const [showGuestForm, setShowGuestForm] = useState(false)
  const [pseudo, setPseudo] = useState('')
  const [isJoining, setIsJoining] = useState(false)
  const [joinError, setJoinError] = useState<string | null>(null)
  const [showErrorModal, setShowErrorModal] = useState(false)
  const [errorModalMessage, setErrorModalMessage] = useState<string | null>(null)

  useEffect(() => {
    const validateToken = async () => {
      if (!token) {
        setError(t('singer.qrCodeMissing'))
        setLoading(false)
        return
      }

      try {
        const response = await apiRequest<EventData>({
          method: 'GET',
          url: API_ENDPOINTS.EVENTS.QR_VALIDATE(token),
        })

        if (response.success && response.data) {
          setEventData(response.data)

          // If user is authenticated, check role and redirect
          if (isAuthenticated && user) {
            if (user.role === 'singer') {
              // Join the event first for authenticated singers
              try {
                const joinResponse = await apiRequest({
                  method: 'POST',
                  url: API_ENDPOINTS.EVENTS.JOIN(response.data.eventId),
                  data: {
                    pseudo: user.profile?.pseudo || user.profile?.firstName || user.email.split('@')[0],
                    guestMode: false,
                  },
                })

                if (!joinResponse.success) {
                  // Check for specific error codes
                  if ((joinResponse as any).code === 'EVENT_NOT_ACTIVE') {
                    setError(t('singer.eventNotActive'))
                    setLoading(false)
                    return
                  }
                  throw new Error(joinResponse.message || t('singer.joinError'))
                }

                navigate(`/singer/${response.data.eventId}`, {
                  replace: true,
                  state: {
                    eventName: response.data.eventName,
                    eventDate: response.data.eventDate,
                    eventStatus: response.data.eventStatus,
                  },
                })
              } catch (err: any) {
                const errorCode = (err as any).code
                if (errorCode === 'EVENT_NOT_ACTIVE') {
                  setError(t('singer.eventNotActive'))
                } else {
                  setError(err.message || t('singer.joinError'))
                }
                setLoading(false)
                return
              }
            } else if (user.role === 'organizer') {
              setError(t('singer.organizerNotAllowed'))
              setLoading(false)
            } else if (user.role === 'superadmin') {
              window.location.href = ADMIN_DASHBOARD_URL
            }
          } else {
            // User not authenticated, show options
            setLoading(false)
          }
        } else {
          // Check for specific error codes before showing generic error
          const errorCode = (response as any).code
          if (errorCode === 'EVENT_NOT_YET_AVAILABLE') {
            setError(t('singer.eventNotYetAvailable') || (response as any).message || 'Event is not yet available. Please wait until the scheduled date.')
          } else {
            setError(t('singer.eventNotFound'))
          }
          setLoading(false)
        }
      } catch (err: any) {
        // Check for specific error codes in catch block
        const errorCode = (err as any).code
        if (errorCode === 'EVENT_NOT_YET_AVAILABLE') {
          setError(t('singer.eventNotYetAvailable') || err.message || 'Event is not yet available. Please wait until the scheduled date.')
        } else {
          setError(err.message || t('singer.qrCodeError'))
        }
        setLoading(false)
      }
    }

    validateToken()
  }, [token, isAuthenticated, user, navigate, t])

  /**
   * @summary Handle guest join.
   */
  const handleGuestJoin = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!eventData || !pseudo.trim()) return

    setIsJoining(true)
    setJoinError(null)

    try {
      // Create a session first
      const sessionResponse = await apiRequest<{ session: { token: string } }>({
        method: 'POST',
        url: API_ENDPOINTS.SESSIONS.CREATE,
        data: {
          eventId: eventData.eventId,
          pseudo: pseudo.trim(),
          guestMode: true,
        },
      })

      if (!sessionResponse.success) {
        // Check for specific error codes
        if ((sessionResponse as any).code === 'EVENT_NOT_ACTIVE') {
          setErrorModalMessage(t('singer.eventNotActive'))
          setShowErrorModal(true)
          setIsJoining(false)
          return
        }
        throw new Error(sessionResponse.message || t('singer.sessionError'))
      }

      const sessionToken = sessionResponse.data?.session?.token

      // Join the event
      const joinResponse = await apiRequest({
        method: 'POST',
        url: API_ENDPOINTS.EVENTS.JOIN(eventData.eventId),
        data: {
          pseudo: pseudo.trim(),
          guestMode: true,
          sessionToken,
        },
      })

      if (!joinResponse.success) {
        throw new Error(joinResponse.message || t('singer.joinError'))
      }

      // Store session and event info in localStorage for guest users
      if (sessionToken) {
        localStorage.setItem('guestSessionToken', sessionToken)
        localStorage.setItem('guestPseudo', pseudo.trim())
        localStorage.setItem('guestEventId', eventData.eventId)
        localStorage.setItem('guestEventName', eventData.eventName)
        if (eventData.eventDate) {
          localStorage.setItem('guestEventDate', eventData.eventDate)
        }
        if (eventData.eventStatus) {
          localStorage.setItem('guestEventStatus', eventData.eventStatus)
        }
      }

      // Navigate to singer page with event info in state
      navigate(`/singer/${eventData.eventId}`, {
        replace: true,
        state: {
          eventName: eventData.eventName,
          eventDate: eventData.eventDate,
          eventStatus: eventData.eventStatus,
        },
      })
    } catch (err: any) {
      // Handle specific error codes
      const errorCode = (err as any).code
      if (errorCode === 'EVENT_NOT_ACTIVE') {
        setErrorModalMessage(t('singer.eventNotActive'))
        setShowErrorModal(true)
      } else {
        setJoinError(err.message || t('singer.joinError'))
      }
    } finally {
      setIsJoining(false)
    }
  }

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center py-8 px-4">
        <div className="text-center">
          <Spinner size="lg" />
          <p className="mt-4 text-(--color-text-secondary)">{t('singer.validatingQRCode')}</p>
        </div>
      </div>
    )
  }

  if (error && !eventData) {
    return (
      <div className="min-h-screen flex items-center justify-center py-8 px-4">
        <div className="w-full page-container">
          <div className="card text-center">
            <div className="text-5xl mb-4">❌</div>
            <h2 className="text-xl font-semibold text-(--color-text-primary) mb-4">{t('common.error')}</h2>
            <p className="text-(--color-text-secondary) mb-6">{error}</p>
            <Link to="/" className="btn btn-primary">
              {t('common.back')}
            </Link>
          </div>
        </div>
      </div>
    )
  }

  // Show guest form
  if (showGuestForm && eventData) {
    return (
      <div className="min-h-screen flex mt-4 justify-center">
        <div className="w-full page-container">
          <div className="card">
            <div className="text-center mb-6">
              <div className="text-5xl mb-4">🎤</div>
              <h1 className="text-2xl font-bold text-(--color-text-primary) mb-2">
                {eventData.eventName}
              </h1>
              <p className="text-(--color-text-secondary)">
                {t('singer.enterPseudo')}
              </p>
            </div>

            {joinError && (
              <div className="alert alert-error mb-4">
                <span>{joinError}</span>
              </div>
            )}

            <form onSubmit={handleGuestJoin} className="space-y-6">
              <FloatingLabelInput
                id="pseudo"
                name="pseudo"
                type="text"
                value={pseudo}
                onChange={(e) => setPseudo(e.target.value)}
                label={t('auth.pseudo')}
                required
              // autoFocus
              />

              <div className="flex flex-col gap-3">
                <button
                  type="submit"
                  className="btn btn-primary w-full"
                  disabled={isJoining || !pseudo.trim()}
                >
                  {isJoining ? (
                    <>
                      <FontAwesomeIcon icon={faSpinner} className="animate-spin mr-2" />
                      {t('singer.joining')}
                    </>
                  ) : (
                    <>
                      <FontAwesomeIcon icon={faMusic} className="mr-2" />
                      {t('singer.joinEvent')}
                    </>
                  )}
                </button>
                <button
                  type="button"
                  onClick={() => setShowGuestForm(false)}
                  className="btn btn-secondary w-full"
                  disabled={isJoining}
                >
                  {t('common.back')}
                </button>
              </div>
            </form>


          </div>
        </div>
        {/* Error Modal for EVENT_NOT_ACTIVE */}
        <ConfirmModal
          isOpen={showErrorModal}
          title={t('common.error') || 'Erreur'}
          message={errorModalMessage || ''}
          variant="warning"
          infoOnly={true}
          onCancel={() => {
            setShowErrorModal(false)
            setErrorModalMessage(null)
            navigate('/')
          }}
        />
      </div>
    )
  }

  // Show login/register/guest options if not authenticated
  if (!isAuthenticated && eventData) {
    return (
      <div className="min-h-screen flex mt-4 justify-center ">
        <div className="w-full page-container">
          <div className="card">
            <div className="text-center mb-8">
              <div className="text-5xl mb-4">🎵</div>
              <h1 className="text-2xl font-bold text-(--color-text-primary) mb-2">
                {eventData.eventName}
              </h1>
              <p className="text-(--color-text-secondary)">
                {t('singer.chooseAccessMethod')}
              </p>
            </div>

            {error && (
              <div className="alert alert-warning mb-4">
                <span>{error}</span>
              </div>
            )}

            <div className="space-y-4">
              {/* Guest mode - quick access */}
              <button
                onClick={() => setShowGuestForm(true)}
                className="btn btn-primary w-full btn-lg flex items-center justify-center gap-2"
              >
                <FontAwesomeIcon icon={faMusic} />
                {t('singer.continueAsGuest')}
              </button>

              <div className="relative my-6">
                <div className="absolute inset-0 flex items-center">
                  <div className="w-full border-t border-(--color-border)"></div>
                </div>
                <div className="relative flex justify-center text-sm">
                  <span className="px-4 bg-(--color-bg-primary) text-(--color-text-secondary)">
                    {t('singer.orWithAccount')}
                  </span>
                </div>
              </div>

              <Link
                to={`/login?redirect=/singer/${eventData.eventId}`}
                className="btn btn-secondary w-full flex items-center justify-center gap-2"
              >
                <FontAwesomeIcon icon={faUser} />
                {t('auth.login')}
              </Link>
              {/* <div className="flex items-center justify-center"> */}
              <Link
                to={`/register?role=singer&redirect=/singer/${eventData.eventId}`}
                className="btn btn-outline w-full! flex items-center justify-center gap-2"
              >
                <FontAwesomeIcon icon={faUserPlus} />
                {t('auth.register')}
              </Link>
              {/* </div> */}
            </div>

            {/* <div className="text-center mt-6">
              <Link
                to="/"
                className="text-sm text-(--color-text-secondary) hover:text-(--color-text-primary)"
              >
                {t('common.back')}
              </Link>
            </div> */}
          </div>
        </div>
      </div>
    )
  }

  // If authenticated organizer sees error message
  if (error && eventData) {
    return (
      <div className="min-h-screen flex items-center justify-center py-8 px-4">
        <div className="w-full page-container">
          <div className="card text-center">
            <div className="text-5xl mb-4">⚠️</div>
            <h2 className="text-xl font-semibold text-(--color-text-primary) mb-4">
              {t('singer.accessDenied')}
            </h2>
            <p className="text-(--color-text-secondary) mb-6">{error}</p>
            <Link to="/dashboard" className="btn btn-primary">
              {t('organizerMenu.dashboard')}
            </Link>
          </div>
        </div>
      </div>
    )
  }

  return null
}
